import { useEffect, useState } from "react";
import { Loader2, Trophy, Users, Swords, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { UserAvatar, UserLink } from "@/components/UserBits";
import { refreshTeams } from "@/lib/teams";
import { TeamChat } from "@/components/TeamChat";
import { TeamRecords } from "@/components/TeamRecords";
import { TeamHall } from "@/components/TeamHall";

const STYLE = { saskia: "from-rose-500 to-pink-400", stefan: "from-slate-800 to-rose-700" };

function TeamCol({ t, d, onJoin, onLeave, busy }) {
  const s = d.scores[t];
  const mine = d.team === t;
  return (
    <div className={`rounded-3xl overflow-hidden border ${mine ? "border-rose-400 ring-2 ring-rose-200" : "border-pink-100"} bg-white`} data-testid={`team-col-${t}`}>
      <div className={`bg-gradient-to-br ${STYLE[t]} text-white p-5`}>
        <p className="font-display text-2xl font-extrabold">{d.names[t]}</p>
        <p className="text-3xl font-extrabold mt-1" data-testid={`team-points-${t}`}>{s.points.toLocaleString("de-DE")}<span className="text-sm font-semibold"> Punkte</span></p>
        <p className="text-xs text-white/80 flex items-center gap-1 mt-1"><Users className="w-3.5 h-3.5" /> {s.members} {s.members === 1 ? "Mitglied" : "Mitglieder"}</p>
      </div>
      <div className="p-4 space-y-3">
        <ul className="space-y-2">
          {s.top.length === 0 && <li className="text-sm text-slate-400">Noch niemand im Team.</li>}
          {s.top.map((x, i) => (
            <li key={x.user.user_id} className="flex items-center gap-2 text-sm"><span className="w-4 text-xs font-bold text-slate-400">{i + 1}</span><UserAvatar userId={x.user.user_id} name={x.user.name} picture={x.user.picture} size="w-7 h-7" text="text-xs" /><UserLink id={x.user.user_id} className="flex-1 min-w-0 truncate font-semibold">{x.user.name}</UserLink><span className="text-xs font-bold text-rose-600">{x.points}</span></li>
          ))}
        </ul>
        {mine ? <>
            <p className="h-11 rounded-full bg-rose-50 text-rose-600 font-bold text-sm flex items-center justify-center" data-testid={`team-mine-${t}`}>Dein Team</p>
            <button onClick={onLeave} disabled={busy} data-testid={`team-leave-${t}`} className="w-full h-9 rounded-full text-xs font-semibold text-slate-500 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center gap-1.5 transition-colors"><LogOut className="w-3.5 h-3.5" /> Aus dem Team austreten</button>
          </>
          : <button onClick={() => onJoin(t)} disabled={busy || (!d.can_change && d.left_team !== t)} data-testid={`team-join-${t}`} className={`w-full h-11 rounded-full bg-gradient-to-r ${STYLE[t]} text-white font-bold text-sm disabled:opacity-40`}>{d.team ? "Team wechseln" : "Beitreten"}</button>}
      </div>
    </div>
  );
}

export default function Teams() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/team").then(({ data }) => setD(data));
  useEffect(() => { load(); }, []);
  const join = async (t) => {
    if (d.team && !window.confirm(`Wirklich zu ${d.names[t]} wechseln? Wechseln geht nur einmal im Monat.`)) return;
    setBusy(true);
    try { await api.post("/team", { team: t }); toast.success(`Willkommen bei ${d.names[t]}!`); refreshTeams().catch(() => {}); await load(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const leave = async () => {
    if (!window.confirm(`Wirklich aus ${d.names[d.team]} austreten? Deine Punkte zählen dann nicht mehr fürs Team. Ein anderes Team kannst du erst nächsten Monat wählen – in dein altes Team kommst du jederzeit zurück.`)) return;
    setBusy(true);
    try { await api.delete("/team"); toast.success("Du bist aus dem Team ausgetreten"); refreshTeams().catch(() => {}); await load(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  const lead = d.scores.saskia.points === d.scores.stefan.points ? null : d.scores.saskia.points > d.scores.stefan.points ? "saskia" : "stefan";
  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="teams-page">
      <div>
        <p className="text-xs tracking-wider uppercase font-semibold text-rose-600 flex items-center gap-1.5"><Swords className="w-4 h-4" /> Monats-Duell {new Date(`${d.month}-01`).toLocaleDateString("de-DE", { month: "long", year: "numeric" })}</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Team Saskia vs. Team Stefan</h1>
        <p className="text-sm text-slate-500 mt-1">Alle Punkte, die du diesen Monat sammelst, zählen für dein Team. Jedes aktive Mitglied des Siegerteams bekommt am Monatsende <b>{d.bonus} Bonus-Punkte</b>. Teamwechsel einmal im Monat.</p>
      </div>
      <p className="text-center font-bold text-rose-600" data-testid="team-lead">{lead ? `${d.names[lead]} führt!` : "Gleichstand!"}{d.team && <span className="text-slate-500 font-semibold"> · Dein Beitrag: {d.my_points} Punkte</span>}</p>
      <div className="grid sm:grid-cols-2 gap-4">{["saskia", "stefan"].map((t) => <TeamCol key={t} t={t} d={d} onJoin={join} onLeave={leave} busy={busy} />)}</div>
      {d.team && <TeamChat key={d.team} teamName={d.names[d.team]} />}
      <TeamRecords />
      <TeamHall />
      {d.last && <p className="text-sm text-center text-slate-500 flex items-center justify-center gap-1.5" data-testid="team-last"><Trophy className="w-4 h-4 text-amber-500" /> Letzter Monat: {d.last.winner ? `${d.names[d.last.winner]} hat gewonnen` : "Unentschieden"} ({d.last.points.saskia} : {d.last.points.stefan})</p>}
    </div>
  );
}
